import parseHtmlToReact from 'html-react-parser';
import type { ComponentType } from 'react';
import { StrictMode, Suspense } from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';

import { ReactQueryProvider } from './ReactQuery';

import { getUniqueId } from '@utilities/random';

const reactRoots = new Map<string, Root>();

/**
 * Inject a React Component in DOM
 */
export const injectReact = <T,>(Component: ComponentType<T>, props: T, container: HTMLElement) => {
  if (!container || !(container instanceof HTMLElement)) return;

  // check if already injected
  if (container.dataset.reactInstanceId) return container;

  container.dataset.reactInstanceId = getUniqueId('react');
  const instanceId = container.dataset.reactInstanceId;

  const root = createRoot(container, { identifierPrefix: instanceId });
  reactRoots.set(instanceId, root);

  const placeholder = container.innerHTML ? parseHtmlToReact(container.innerHTML) : null;

  root.render(
    <StrictMode>
      <ReactQueryProvider>
        <Suspense fallback={placeholder}>
          <Component {...props} placeholder={placeholder} />
        </Suspense>
      </ReactQueryProvider>
    </StrictMode>,
  );

  return container;
};

export const unmountReact = (container: HTMLElement) => {
  if (!container || !(container instanceof HTMLElement)) return;

  const instanceId = container.dataset.reactInstanceId;
  if (!instanceId) return;

  const root = reactRoots.get(instanceId);
  if (!root) return;

  root.unmount();
  reactRoots.delete(instanceId);
};
